import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Event } from '../types';

interface CompactEventCalendarProps {
  events: Event[];
}

export const CompactEventCalendar: React.FC<CompactEventCalendarProps> = ({ events }) => {
  const navigate = useNavigate();
  const [displayDate, setDisplayDate] = useState(new Date());
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);
  const currentDate = new Date();
  const currentMonth = displayDate.getMonth();
  const currentYear = displayDate.getFullYear();

  // Get the first day of the month and number of days
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  // Get next upcoming event
  const nextEvent = events
    .filter((event) => new Date(event.event_date) >= new Date())
    .sort(
      (a, b) =>
        new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
    )[0];

  // Get event dates for this month
  const eventDates = events
    .filter((event) => {
      const eventDate = new Date(event.event_date);
      return (
        eventDate.getMonth() === currentMonth &&
        eventDate.getFullYear() === currentYear
      );
    })
    .map((event) => new Date(event.event_date).getDate());

  // Map each day of the month to its events (for hover popup)
  const eventsByDay = events.reduce<Record<number, Event[]>>((acc, event) => {
    const eventDate = new Date(event.event_date);
    if (
      eventDate.getMonth() === currentMonth &&
      eventDate.getFullYear() === currentYear
    ) {
      const day = eventDate.getDate();
      (acc[day] = acc[day] || []).push(event);
    }
    return acc;
  }, {});

  // Get next event date for highlighting
  const nextEventDate = nextEvent ? new Date(nextEvent.event_date) : null;
  const isNextEventThisMonth =
    nextEventDate &&
    nextEventDate.getMonth() === currentMonth &&
    nextEventDate.getFullYear() === currentYear;

  // Generate calendar days
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const goToPreviousMonth = () => {
    setDisplayDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setDisplayDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToCurrentMonth = () => {
    setDisplayDate(new Date());
  };

  return (
    <div className='bg-white rounded-lg p-2.5 border border-gray-200'>
      {/* Month Navigation Header */}
      <div className='flex items-center justify-between mb-1.5'>
        <button
          onClick={goToPreviousMonth}
          className='p-1 rounded-full hover:bg-gray-100 transition-colors'
          aria-label='Mois précédent'
        >
          <svg
            className='w-4 h-4 text-gray-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M15 19l-7-7 7-7'
            />
          </svg>
        </button>

        <button
          onClick={goToCurrentMonth}
          className='text-center text-sm font-semibold text-church-brown hover:text-church-gold transition-colors cursor-pointer'
        >
          {monthNames[currentMonth]} {currentYear}
        </button>

        <button
          onClick={goToNextMonth}
          className='p-1 rounded-full hover:bg-gray-100 transition-colors'
          aria-label='Mois suivant'
        >
          <svg
            className='w-4 h-4 text-gray-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M9 5l7 7-7 7'
            />
          </svg>
        </button>
      </div>

      {/* Day headers */}
      <div className='grid grid-cols-7 gap-0.5 mb-1.5'>
        {dayNames.map((day) => (
          <div key={day} className='text-center text-xs font-medium text-gray-600 py-0.5'>
            {day}
          </div>
        ))}
      </div>

            {/* Calendar grid */}
            <div className='grid grid-cols-7 gap-0.5'>
        {calendarDays.map((day, index) => {
          const isToday =
            day === currentDate.getDate() &&
            currentMonth === currentDate.getMonth() &&
            currentYear === currentDate.getFullYear();
          const isNextEventDay =
            isNextEventThisMonth && day === nextEventDate?.getDate();
          const hasEvent = day && eventDates.includes(day) && !isNextEventDay;
          const dayEvents = day ? eventsByDay[day] || [] : [];
          const hasAnyEvent = dayEvents.length > 0;
          // Rows 0-1 open the popup below the cell to avoid clipping at the top
          const openBelow = index < 14;
          // Anchor edge columns to their side to avoid horizontal clipping
          const col = index % 7;
          const horizontalClass =
            col === 0
              ? 'left-0'
              : col === 6
              ? 'right-0'
              : 'left-1/2 -translate-x-1/2';
          const arrowClass =
            col === 0 ? 'left-3' : col === 6 ? 'right-3' : 'left-1/2 -translate-x-1/2';

          return (
            <div
              key={index}
              className={`relative flex justify-center ${hasAnyEvent ? 'group' : ''}`}
              onMouseEnter={() => hasAnyEvent && setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              <div
                onClick={() =>
                  hasAnyEvent && navigate(`/events/${dayEvents[0].id}`)
                }
                className={`
                  w-7 h-7 flex items-center justify-center text-xs rounded transition-colors
                  ${day === null ? '' : 'hover:bg-gray-100'}
                  ${hasAnyEvent ? 'cursor-pointer' : ''}
                  ${
                    isNextEventDay
                      ? 'bg-church-gold text-white font-bold ring-1 ring-church-gold'
                      : isToday
                      ? 'bg-church-brown text-white font-bold'
                      : hasEvent
                      ? 'bg-church-gold/50 text-church-brown font-semibold'
                      : day
                      ? 'bg-gray-50 text-gray-700'
                      : ''
                  }
                `}
              >
                {day}
              </div>

              {/* Hover popup with the day's events */}
              {hasAnyEvent && hoveredDay === day && (
                <div
                  className={`absolute ${horizontalClass} z-50 w-48 rounded-lg bg-white shadow-xl ring-1 ring-gray-200 p-2 ${
                    openBelow ? 'top-full mt-1' : 'bottom-full mb-1'
                  }`}
                >
                  <p className='text-xs font-semibold text-church-brown mb-1'>
                    {day} {monthNames[currentMonth]}
                  </p>
                  <div className='space-y-1.5'>
                    {dayEvents.map((event) => (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className='block rounded p-1 -m-1 hover:bg-gray-50 transition-colors'
                      >
                        <p className='text-xs font-medium text-gray-800 line-clamp-2'>
                          {event.title}
                        </p>
                        <p className='text-[11px] text-gray-500 mt-0.5'>
                          {new Date(event.event_date).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {event.location ? ` · ${event.location}` : ''}
                        </p>
                      </Link>
                    ))}
                  </div>
                  {/* Little arrow */}
                  <div
                    className={`absolute ${arrowClass} w-2 h-2 rotate-45 bg-white ${
                      openBelow ? '-top-1' : '-bottom-1'
                    }`}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className='mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs'>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 bg-church-brown rounded'></div>
          <span className='text-gray-600'>Aujourd'hui</span>
        </div>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 bg-church-gold rounded ring-1 ring-church-gold ring-offset-1'></div>
          <span className='text-gray-600'>Prochain</span>
        </div>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 bg-church-gold/50 rounded'></div>
          <span className='text-gray-600'>Événement</span>
        </div>
      </div>

      {/* Next Event Info */}
      {nextEvent && (
        <div className='mt-2 pt-2 border-t border-gray-200'>
          <p className='text-xs text-gray-500 mb-1'>Prochain événement:</p>
          <Link
            to={`/events/${nextEvent.id}`}
            className='text-xs font-semibold text-church-brown hover:text-church-gold transition-colors line-clamp-1'
          >
            {nextEvent.title}
          </Link>
          <p className='text-xs text-gray-600 mt-0.5'>
            {new Date(nextEvent.event_date).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
